import type { MatchMap } from "../model/Analysis";
import type { TimelineEvent } from "./TimelineEvent";
import { UV_TO_WORLD, WORLD_SIZE } from "./WORLD_SIZE";

const world = (value: number): number => value * UV_TO_WORLD;

// События шкалы времени по порядку: твои смерти и убийства, мид-босс, разрушенные здания,
// руны на мостах, которые взял ты.
export const timelineEvents = (match: MatchMap): TimelineEvent[] => {
  const me = match.player_slot;
  const heroOf = new Map(match.players.map((player) => [player.slot, player.hero_id]));
  const myDeaths = match.players.find((player) => player.slot === me)?.deaths ?? [];
  const events: TimelineEvent[] = [
    ...match.deaths.flatMap((death): TimelineEvent[] => {
      const place = { at: death.at_s, x: world(death.u), y: world(death.v) };
      if (death.victim_slot === me) {
        return [
          {
            ...place,
            kind: "death",
            killerHeroId:
              death.killer_slot === null ? null : (heroOf.get(death.killer_slot) ?? null),
            deadForS: myDeaths.find(([at]) => at === death.at_s)?.[1] ?? 0,
          },
        ];
      }
      const victimHeroId = heroOf.get(death.victim_slot);
      return death.killer_slot === me && victimHeroId !== undefined
        ? [{ ...place, kind: "kill", victimHeroId }]
        : [];
    }),
    ...match.mid_boss.map((kill): TimelineEvent => ({
      at: kill.killed_at_s,
      x: WORLD_SIZE / 2,
      y: WORLD_SIZE / 2,
      kind: "boss",
      ours: kill.team_claimed === match.team,
    })),
    ...match.objectives.flatMap((objective): TimelineEvent[] =>
      objective.destroyed_at_s === null
        ? []
        : [
            {
              at: objective.destroyed_at_s,
              x: world(objective.u),
              y: world(objective.v),
              kind: "objective",
              objective: objective.kind,
              ours: objective.team === match.team,
            },
          ],
    ),
    ...match.runes.flatMap((rune): TimelineEvent[] =>
      rune.taken_by_slot === me && rune.taken_at_s !== null
        ? [{ at: rune.taken_at_s, x: world(rune.u), y: world(rune.v), kind: "rune" }]
        : [],
    ),
  ];
  return events.sort((a, b) => a.at - b.at);
};

// Ближайшее к моменту `t` событие не дальше `window` секунд; нет — `null`.
export const nearestEvent = (
  events: readonly TimelineEvent[],
  t: number,
  window: number,
): TimelineEvent | null =>
  events.reduce<TimelineEvent | null>((best, event) => {
    const distance = Math.abs(event.at - t);
    if (distance > window) {
      return best;
    }
    return best === null || distance < Math.abs(best.at - t) ? event : best;
  }, null);

// Без смертей карта открывается на 10:00 — появляется мид-босс, линии уже позади.
const OPENING_WITHOUT_DEATHS_S = 600;

// С чего открыть карту: твоя первая смерть с её карточкой; смертей нет — 10:00 без карточки.
export const openingMoment = (
  events: readonly TimelineEvent[],
  durationS: number,
): { t: number; event: TimelineEvent | null } => {
  const death = events.find((event) => event.kind === "death");
  return death === undefined
    ? { t: Math.min(OPENING_WITHOUT_DEATHS_S, durationS), event: null }
    : { t: death.at, event: death };
};

// Соседнее событие: от показанного в карточке — по списку, без карточки — от момента `t`.
export const adjacentEvent = (
  events: readonly TimelineEvent[],
  from: TimelineEvent | null,
  t: number,
  direction: 1 | -1,
): TimelineEvent | null => {
  const index = from === null ? -1 : events.indexOf(from);
  if (index >= 0) {
    return events[index + direction] ?? null;
  }
  return direction === 1
    ? (events.find((event) => event.at > t) ?? null)
    : (events.findLast((event) => event.at < t) ?? null);
};
